import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, filter, Subject, takeUntil } from 'rxjs';
import { CustomerAuthService } from 'src/app/public/customer-auth.service';
import { LocationService } from 'src/app/public/location.service';
import { ConfirmDialogComponent } from 'src/app/shared/dialog/confirm-dialog/confirm-dialog.component';
import { ClientStore } from 'src/app/stores/stores';
import { Utils } from 'src/app/stores/utils/Utils';
import {
  DeleteCustomerAddress,
  UpdateCustomerAddress,
} from '../../+state/stores.actions';
import { SelectedStoreState } from '../../+state/stores.reducer';
import {
  getCurrentStoreViewStatus,
  getCustomerProfileState,
  getCustomerProfileUpdateStatus,
  getSelectedStore,
} from '../../+state/stores.selectors';
import {
  CustomerAddress,
  CustomerProfile,
} from '../../types/CustomerSocialLogin';

@Component({
  selector: 'app-edit-address',
  templateUrl: './edit-address.component.html',
  styleUrl: './edit-address.component.scss',
})
export class EditAddressComponent implements OnInit, OnDestroy {
  unsubscribe$: Subject<void> = new Subject<void>();

  addressId: string;

  profileForm: FormGroup;
  customerProfile: CustomerProfile;
  selectedStore: ClientStore;
  loading: boolean = false;
  status: 'INITIAL' | 'LOADING' | 'SUCCESS' | 'FAILED';
  updateStatus: 'INITIAL' | 'UPDATING' | 'SUCCESS' | 'FAILED';
  customerAddress: CustomerAddress;

  constructor(
    private fb: FormBuilder,
    private store: Store<SelectedStoreState>,
    private customerAuthService: CustomerAuthService,
    private locationService: LocationService,
    private router: Router,
    private translateService: TranslateService,
    public dialog: MatDialog,
  ) {}

  ngOnInit() {
    this.addressId = this.locationService.getParamAt(1);
    this.customerAddress = null;
    this.createForm();

    combineLatest([
      this.store.select(getSelectedStore),
      this.store.select(getCurrentStoreViewStatus),
      this.store.select(getCustomerProfileState),
    ])
      .pipe(
        takeUntil(this.unsubscribe$),
        filter(
          ([store, status, profileState]) =>
            !!store?.id &&
            status.state === 'EDITADDRESS' &&
            profileState.status === 'SUCCESS',
        ),
      )
      .subscribe(([store, status, profileState]) => {
        if (!this.customerAuthService.isUserLoggedIn()) {
          this.router.navigateByUrl(this.locationService.base_url('login'));
        }
        this.selectedStore = store;
        this.customerProfile = profileState.data;

        if (this.addressId) {
          this.customerAddress = this.customerProfile?.addresses?.find(
            (address) => address.id === +this.addressId,
          );
          if (!this.customerAddress) {
            this.returnToProfile();
          }
        }

        if (
          this.status !== 'SUCCESS' &&
          profileState.status === 'SUCCESS' &&
          this.customerAddress
        ) {
          this.profileForm.patchValue({
            streetAddress: this.customerAddress?.streetAddress ?? '',
            city: this.customerAddress?.city ?? '',
            postCode: this.customerAddress?.postCode ?? '',
            floorNumber: this.customerAddress?.floorNumber ?? '',
            doorbell: this.customerAddress?.doorbell ?? '',
            isMain: this.customerAddress?.isMain ?? false,
          });
        }

        this.status = profileState.status;
      });

    this.store
      .select(getCustomerProfileUpdateStatus)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((updateStatus) => {
        this.loading = updateStatus === 'UPDATING';

        if (this.updateStatus === 'UPDATING' && updateStatus === 'SUCCESS') {
          this.router.navigateByUrl(this.locationService.base_url('profile'));
        }

        if (this.updateStatus === 'UPDATING' && updateStatus === 'FAILED') {
          console.error('Failed to update profile');
        }

        this.updateStatus = updateStatus;
      });
  }

  createForm() {
    this.profileForm = this.fb.group({
      streetAddress: [
        this.customerAddress?.streetAddress ?? '',
        Validators.compose([
          Validators.required,
          Validators.minLength(2),
          Validators.maxLength(200),
        ]),
      ],
      city: [
        this.customerAddress?.city ?? '',
        Validators.compose([
          Validators.required,
          Validators.minLength(2),
          Validators.maxLength(100),
        ]),
      ],
      postCode: [
        this.customerAddress?.postCode ?? '',
        Validators.compose([
          Validators.required,
          Validators.minLength(2),
          Validators.maxLength(20),
        ]),
      ],
      floorNumber: [
        this.customerAddress?.floorNumber ?? '',
        Validators.compose([Validators.minLength(0), Validators.maxLength(20)]),
      ],
      doorbell: [
        this.customerAddress?.doorbell ?? '',
        Validators.compose([Validators.minLength(0), Validators.maxLength(20)]),
      ],
      isMain: [this.customerAddress?.isMain ?? false, Validators.required],
    });
  }

  editMode(): boolean {
    return !!this.addressId;
  }

  getControl(name: string): AbstractControl {
    return this.profileForm?.get(name);
  }

  handleSubmit(event: Event) {
    event.preventDefault();

    if (this.profileForm.valid && !this.loading) {
      this.loading = true;
      this.store.dispatch(
        new UpdateCustomerAddress(
          {
            id: this.customerAddress?.id,
            streetAddress: this.profileForm.get('streetAddress')?.value,
            city: this.profileForm.get('city')?.value,
            postCode: this.profileForm.get('postCode')?.value,
            floorNumber: this.profileForm.get('floorNumber')?.value,
            doorbell: this.profileForm.get('doorbell')?.value,
            isMain: this.profileForm.get('isMain')?.value,
          },
          {
            src: 'EditAddressComponent',
            description: 'Update customer address',
          },
        ),
      );
    } else {
      this.profileForm.markAllAsTouched();
    }
  }

  handleCancel(event: Event) {
    event.preventDefault();
    this.returnToProfile();
  }

  returnToProfile() {
    this.router.navigateByUrl(this.locationService.base_url('profile'));
  }

  handleDelete(event: Event) {
    event.preventDefault();

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '540px',
      data: {
        title: this.translateService.instant('public.profile.address.removeAddress'),
        message: this.translateService.instant(
          'public.profile.address.deleteConfirmMessage',
          { address: Utils.formatAddress(this.customerAddress) },
        ),
      },
    });
    
    dialogRef.afterClosed().subscribe((result) => {
      if (result && this.customerAddress?.id && !this.loading) {
        this.loading = true;
        this.store.dispatch(
          new DeleteCustomerAddress(this.customerAddress.id, {
            src: 'EditAddressComponent',
            description: 'Delete customer address',
          }),
        );
      }
    });
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
